import { useEffect, useState } from "react";
import "./DownloadHelp.scss";

type OS = "windows" | "mac";

export default function DownloadHelp() {
    useEffect(() => {
        document.title = 'Download Help'
    }, [])

    const [os, setOs] = useState<OS>("windows");

    return (
        <main className="download-help-page">
            <section className="download-help-card">
                <p className="eyebrow">FlashRoom Download Help</p>

                <h1>Download blocked or failed?</h1>

                <p className="intro">
                    FlashRoom tried to download your file, but your browser,
                    Google account, school/work security settings, or an
                    extension may have stopped it.
                </p>

                <div className="notice-box">
                    <strong>Try holding shift in refreshing the browser</strong>
                    <span>
                        And older version of FlashRoom may be cached, and refreshing while holding shift forces the browser to fetch the latest version. This can resolve issues caused by an outdated cached version of FlashRoom.
                    </span>
                </div>


                <section className="download-help">
                    <div className="trust-warning">
                        <strong>Only download files from people you trust.</strong>
                        FlashRoom is meant for trusted educators, classmates, coworkers, or peers. Do not download or open files from an unknown or suspicious source. Only proceed to the next steps if you trust the person who shared the file.
                    </div>

                    <h2>Fastest workarounds</h2>

                    <div className="workaround-grid">
                        <div className="workaround-card">
                            <span className="step-number">1</span>
                            <h3>Try incognito/private mode</h3>
                            <p>Open FlashRoom in an incognito/private window and download the file again.</p>
                        </div>

                        <div className="workaround-card">
                            <span className="step-number">2</span>
                            <h3>Try another browser</h3>
                            <p>If Chrome blocks it, try Edge, Safari, Firefox, or another Chrome profile.</p>
                        </div>

                        <div className="workaround-card">
                            <span className="step-number">3</span>
                            <h3>Zip the file</h3>
                            <p>Ask the teacher to place the file in a folder, compress it into a .zip, and upload the zip instead.</p>
                        </div>

                        <div className="workaround-card">
                            <span className="step-number">4</span>
                            <h3>Rename the file</h3>
                            <p>Remove unusual characters, extra periods, or symbols from the filename, then upload it again.</p>
                        </div>
                    </div>
                </section>


                <section className="help-section">
                    <h2>Browser and Google account checks</h2>

                    <div className="instruction-list">
                        <div className="instruction-item">
                            <h3>Check Chrome downloads</h3>
                            <p>
                                In Chrome, open the downloads page and look for
                                a message saying the file was blocked.
                            </p>
                            <code>chrome://downloads</code>
                        </div>

                        <div className="instruction-item">
                            <h3>Check Safe Browsing</h3>
                            <p>
                                In Chrome settings, search for “Safe Browsing”.
                                If Enhanced Protection is enabled, try Standard
                                Protection if your account allows it.
                            </p>
                        </div>

                        <div className="instruction-item">
                            <h3>Try a personal Google account</h3>
                            <p>
                                School or work Google accounts may have stricter
                                rules than personal accounts.
                            </p>
                        </div>

                        <div className="instruction-item">
                            <h3>Disable extensions temporarily</h3>
                            <p>
                                Ad blockers, antivirus extensions, privacy tools,
                                or school security extensions can interfere with
                                downloads.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="help-section">
                    <div className="section-heading-row">
                        <h2>System-specific checks</h2>

                        <div className="os-toggle" aria-label="Operating system toggle">
                            <button
                                className={os === "windows" ? "active" : ""}
                                onClick={() => setOs("windows")}
                                type="button"
                            >
                                Windows
                            </button>

                            <button
                                className={os === "mac" ? "active" : ""}
                                onClick={() => setOs("mac")}
                                type="button"
                            >
                                Mac
                            </button>
                        </div>
                    </div>

                    {os === "windows" ? (
                        <div className="os-panel">
                            <h3>Windows steps</h3>

                            <ol>
                                <li>
                                    Open your browser downloads page and check if
                                    the file was blocked.
                                </li>
                                <li>
                                    Try downloading to your regular Downloads
                                    folder instead of a synced or restricted folder.
                                </li>
                                <li>
                                    If Windows Security or antivirus software
                                    shows a warning, ask a teacher/admin before
                                    allowing the file.
                                </li>
                                <li>
                                    Try Edge or a fresh Chrome profile if the
                                    current Chrome profile blocks it.
                                </li>
                            </ol>
                        </div>
                    ) : (
                        <div className="os-panel">
                            <h3>Mac steps</h3>

                            <ol>
                                <li>
                                    Open your browser downloads page and check if
                                    the file was blocked.
                                </li>
                                <li>
                                    Try downloading to your regular Downloads
                                    folder.
                                </li>
                                <li>
                                    If macOS shows a security warning, ask a
                                    teacher/admin before opening or allowing the file.
                                </li>
                                <li>
                                    Try Safari or a fresh Chrome profile if the
                                    current Chrome profile blocks it.
                                </li>
                            </ol>
                        </div>
                    )}
                </section>

                <section className="help-section">
                    <h2>For teachers</h2>

                    <div className="teacher-box">
                        <p>
                            If students cannot download a specific file, try
                            uploading the file as a zip.
                        </p>

                        <ol>
                            <li>Create a new folder.</li>
                            <li>Put the blocked file inside the folder.</li>
                            <li>Compress the folder into a .zip file.</li>
                            <li>Upload the .zip file to FlashRoom.</li>
                        </ol>

                        <p>
                            This often avoids browser confusion with uncommon
                            file types like packet tracer files, project files,
                            or other classroom-specific formats.
                        </p>
                    </div>
                </section>
            </section>
        </main>
    );
}